// log-retention-manager.ts
//
// §18 log retention + min free disk (phase-08 Agent H, tests 25-26). Two
// independent responsibilities:
//   1. `pruneAttemptLogs` — deletes raw attempt evidence files under
//      `<control_root>/attempts/<attemptId>/forensics/**` once they exceed
//      `logs.retention_days` OR once the total size of ALL such files
//      exceeds `logs.max_total_mb` (oldest mtime first until under cap).
//      NEVER touches `journal.jsonl`/`mission.json` at the control root
//      (those aren't under attempts/), and NEVER touches a still-open
//      attempt's forensics (caller supplies `openAttemptIds` — an attempt
//      not yet routed to a terminal outcome still needs its evidence for
//      verification/G2 review).
//   2. `checkMinFreeDisk` — `fs.promises.statfs` (Node >=18.15) against the
//      configured mount, block a new attempt when free space is below
//      `min_free_disk_mb`.
//
// Both thresholds (`logs.retention_days`, `logs.max_total_mb`,
// `min_free_disk_mb`) come from the injected AutopilotConfig slice — no
// literal (AST gate extended to cover this file, same as
// quiet-hours-scheduler.ts).
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { AutopilotConfig } from "./schemas/config-schema.js";

export type LogRetentionConfig = Pick<AutopilotConfig, "logs">;
export type MinFreeDiskConfig = Pick<AutopilotConfig, "min_free_disk_mb">;

export interface PruneAttemptLogsInput {
  controlRoot: string;
  config: LogRetentionConfig;
  now: Date;
  /**
   * Attempt IDs not yet at a terminal outcome (no closed receipt) — their
   * forensics are never pruned. `undefined` means the caller could not
   * determine which attempts are still open — fails CLOSED (prunes nothing)
   * rather than defaulting to "treat everything as closed" (code-reviewer-
   * 260909-p8-m4.md H2: an omitted list must never be silently read as "all
   * attempts are done, safe to delete").
   */
  openAttemptIds?: readonly string[];
}

export interface PruneAttemptLogsResult {
  prunedPaths: string[];
  bytesFreed: number;
}

interface CandidateFile {
  path: string;
  attemptId: string;
  size: number;
  mtimeMs: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Recursively lists every regular file under `<controlRoot>/attempts/<id>/**`, tagged with its attemptId. */
async function listAttemptFiles(controlRoot: string): Promise<CandidateFile[]> {
  const attemptsDir = join(controlRoot, "attempts");
  const out: CandidateFile[] = [];

  let attemptDirs: string[];
  try {
    attemptDirs = await fs.readdir(attemptsDir);
  } catch {
    return out; // No attempts/ yet — nothing to prune.
  }

  for (const attemptId of attemptDirs) {
    const root = join(attemptsDir, attemptId);
    const stack = [root];
    while (stack.length > 0) {
      const dir = stack.pop();
      if (dir === undefined) continue;
      let entries: import("node:fs").Dirent[];
      try {
        entries = await fs.readdir(dir, { withFileTypes: true });
      } catch {
        continue;
      }
      for (const entry of entries) {
        const full = join(dir, entry.name);
        if (entry.isDirectory()) {
          stack.push(full);
        } else if (entry.isFile()) {
          const st = await fs.stat(full);
          out.push({ path: full, attemptId, size: st.size, mtimeMs: st.mtimeMs });
        }
      }
    }
  }
  return out;
}

/**
 * Deletes attempt log/forensics files past `retention_days`, then — if the
 * REMAINING total still exceeds `max_total_mb` — deletes oldest-mtime-first
 * until under the cap. Files belonging to `openAttemptIds` are excluded from
 * consideration entirely (protected regardless of age or size).
 */
export async function pruneAttemptLogs(
  input: PruneAttemptLogsInput,
): Promise<PruneAttemptLogsResult> {
  // Fail closed: caller didn't/couldn't supply openAttemptIds => treat as
  // "open-attempt state unknown", not "no attempts are open". Pruning
  // nothing is always safe; pruning an open attempt's forensics is not.
  if (input.openAttemptIds === undefined) {
    return { prunedPaths: [], bytesFreed: 0 };
  }

  const openSet = new Set(input.openAttemptIds);
  const all = await listAttemptFiles(input.controlRoot);
  const eligible = all.filter((f) => !openSet.has(f.attemptId));

  const prunedPaths: string[] = [];
  let bytesFreed = 0;

  const retentionCutoffMs = input.now.getTime() - input.config.logs.retention_days * MS_PER_DAY;
  const remaining: CandidateFile[] = [];
  for (const file of eligible) {
    if (file.mtimeMs < retentionCutoffMs) {
      await fs.rm(file.path, { force: true });
      prunedPaths.push(file.path);
      bytesFreed += file.size;
    } else {
      remaining.push(file);
    }
  }

  const capBytes = input.config.logs.max_total_mb * 1024 * 1024;
  let totalRemaining = remaining.reduce((sum, f) => sum + f.size, 0);
  if (totalRemaining > capBytes) {
    // Oldest mtime first.
    remaining.sort((a, b) => a.mtimeMs - b.mtimeMs);
    for (const file of remaining) {
      if (totalRemaining <= capBytes) break;
      await fs.rm(file.path, { force: true });
      prunedPaths.push(file.path);
      bytesFreed += file.size;
      totalRemaining -= file.size;
    }
  }

  return { prunedPaths, bytesFreed };
}

export interface CheckMinFreeDiskInput {
  config: MinFreeDiskConfig;
  path: string;
  /** Injectable — tests never require a real filesystem's actual free space. */
  statfs?: (path: string) => Promise<{ bavail: number; bsize: number }>;
}

export interface MinFreeDiskDecision {
  blocked: boolean;
  freeMb: number;
  reason?: string;
}

async function defaultStatfs(path: string): Promise<{ bavail: number; bsize: number }> {
  const result = await fs.statfs(path);
  return { bavail: result.bavail, bsize: result.bsize };
}

/** Blocks a new dispatch attempt when free disk at `path`'s mount is below `min_free_disk_mb`. */
export async function checkMinFreeDisk(input: CheckMinFreeDiskInput): Promise<MinFreeDiskDecision> {
  const statfs = input.statfs ?? defaultStatfs;
  const { bavail, bsize } = await statfs(input.path);
  const freeMb = (bavail * bsize) / (1024 * 1024);
  const threshold = input.config.min_free_disk_mb;
  if (freeMb < threshold) {
    return {
      blocked: true,
      freeMb,
      reason: `Đĩa còn ${freeMb.toFixed(1)}MB, dưới ngưỡng min_free_disk_mb=${threshold}MB — chặn attempt mới.`,
    };
  }
  return { blocked: false, freeMb };
}
